const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfDay(date) {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

/** ISO week number (1–53) for a date. */
export function getISOWeek(date) {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil((((target - yearStart) / MS_PER_DAY) + 1) / 7);
}

/** ISO week-year (may differ from calendar year near Jan 1). */
export function getISOWeekYear(date) {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  return target.getUTCFullYear();
}

export function formatWeekKey(year, week) {
  return `${year}-W${String(week).padStart(2, '0')}`;
}

export function parseWeekKey(weekKey) {
  const match = /^(\d{4})-W(\d{2})$/.exec(String(weekKey || ''));
  if (!match) return null;
  return { year: Number(match[1]), week: Number(match[2]) };
}

/** Last day (Sunday) of ISO week at 23:59:59.999 local time. */
export function weekKeyToDueDate(weekKey) {
  const parsed = parseWeekKey(weekKey);
  if (!parsed) return null;

  const jan4 = new Date(parsed.year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const weekOneMonday = new Date(jan4);
  weekOneMonday.setDate(jan4.getDate() - jan4Day + 1);

  const weekStart = new Date(weekOneMonday);
  weekStart.setDate(weekOneMonday.getDate() + (parsed.week - 1) * 7);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  return weekEnd;
}

export function dueDateToWeekKey(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatWeekKey(getISOWeekYear(date), getISOWeek(date));
}

export function getCurrentWeekKey(now = new Date()) {
  return formatWeekKey(getISOWeekYear(now), getISOWeek(now));
}

/**
 * @param {{ weeksBefore?: number, weeksAfter?: number, now?: Date }} options
 * @returns {Array<{ label: string, value: string }>}
 */
export function buildCalendarWeekOptions(options = {}) {
  const {
    weeksBefore = 0,
    weeksAfter = 12,
    now = new Date(),
    translate,
  } = options;

  const currentKey = getCurrentWeekKey(now);
  const parsed = parseWeekKey(currentKey);
  if (!parsed) return [];

  const anchor = weekKeyToDueDate(currentKey);
  if (!anchor) return [];

  const currentWeekLabel = translate
    ? translate('UserProjects.commitmentDialog.currentWeekSuffix')
    : null;

  const optionsList = [];
  for (let offset = -weeksBefore; offset <= weeksAfter; offset += 1) {
    const date = new Date(anchor);
    date.setDate(anchor.getDate() + offset * 7);
    const key = formatWeekKey(getISOWeekYear(date), getISOWeek(date));
    const week = getISOWeek(date);
    const isCurrent = key === currentKey;
    optionsList.push({
      label: isCurrent && currentWeekLabel ? `KW ${week} ${currentWeekLabel}` : `KW ${week}`,
      value: key,
    });
  }

  const seen = new Set();
  return optionsList.filter((item) => {
    if (seen.has(item.value)) return false;
    seen.add(item.value);
    return true;
  });
}

export function formatWeekLabelFromKey(weekKey, translate) {
  const parsed = parseWeekKey(weekKey);
  if (!parsed) return '';
  const currentKey = getCurrentWeekKey();
  const suffix = weekKey === currentKey && translate
    ? ` ${translate('UserProjects.commitmentDialog.currentWeekSuffix')}`
    : '';
  return `KW ${parsed.week}${suffix}`;
}

export { startOfDay };
